import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, WritableSignal, effect, forwardRef, inject, input, model, output } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { simpleDeepClone } from '@phx-erp/shared';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { TruncatePipe } from '../../../../../@core/pipes';
import { EditLinks } from '../../../../../@core/constants/edit-links';
import { NotificationService } from '../../../../../@core/services/notification/notification.service';
import { insertBreakAtDot } from '../../../../../@core/utils/helper';
import { Asset, AssetInput, Product, ProductInput, ProductType } from '../../../../../generated/generated-types';
import { WidgetAssetPreviewComponent } from '../../../../../modules/asset/widget-asset-preview/widget-asset-preview.component';
import { BaseModelInfoWidgetComponent, formatCreatorEmployeeName } from '../../../../../shared/components/base-model-info-widget/base-model-info-widget.component';
import { PhxCardComponent } from '../../../../../shared/components/phx-card/phx-card.component';
import { AssetService } from '../../../../asset/services/asset.service';
import { ProductService } from '../../../services/product.service';


@Component({
  selector: 'app-product-header-card-widget',
  templateUrl: './product-header-card-widget.component.html',
  styleUrl: './product-header-card-widget.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    PhxCardComponent,
    WidgetAssetPreviewComponent,
    BadgeModule,
    TooltipModule,
    TranslatePipe,
    TruncatePipe,
    BaseModelInfoWidgetComponent,
    ButtonModule,
    RouterModule
  ],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => ProductHeaderCardWidgetComponent),
    multi: true
  }]
})
export class ProductHeaderCardWidgetComponent implements ControlValueAccessor {
  assetService = inject(AssetService);
  productService = inject(ProductService);
  notificationService = inject(NotificationService);
  router = inject(Router);

  formControl = input.required<FormControl>();
  key = input.required<string>();
  form = input.required<FormGroup>();

  product = model<Product>();
  loadingSig = input<WritableSignal<boolean>>();
  onDelete = output<boolean>();

  assetModel: Asset;

  borderLeftColor = model<string>("#505050"); //gray

  onChange: (value: Asset) => void = (value) => { this.assetModel = value }; // Value Accessor Related
  onTouch: () => void = () => { }; // Value Accessor Related

  constructor(
  ) {

    effect(() => {

      if (!this.product()) {
        this.borderLeftColor.set("#505050"); //gray
        return;
      }

      // refreshes the asset if the product is new
      if (this.product()) {
        this.getAsset();
      }

      if (this.product()?.variantParentId != null) {
        this.borderLeftColor.set("#FFDAB9 "); //Peach
        return;
      }

      switch (this.product()?.productType) {
        case ProductType.VARIANT:
          this.borderLeftColor.set("#FFA07A"); //Light Salmon
          break;
        default:
          this.borderLeftColor.set("#0d6efd"); //Blue
      }
    });

  }

  ngOnChanges(changes: any) {
    if (changes?.product?.currentValue && Object.keys(changes?.product?.currentValue).length !== 0 && changes.product?.currentValue !== changes.address?.previousValue) {
      this.product.set(simpleDeepClone(changes.product.currentValue));
    }
  }

  async onFilesSelected(files: File[]) {

    if (files?.length == 1) {

      let input: AssetInput = {
        file: files[0],
        name: "test"
      }

      let res = await this.assetService.saveAsset(input);
      this.assetModel = res;
      // this.cdRef.detectChanges();

      let savedProduct: Product;
      let updateProduct: ProductInput = {
        id: this.product().id,
        featuredAssetId: res.id
      };
      savedProduct = await this.productService.saveItem(updateProduct);

      // if used as formly component
      if (this.formControl())
        this.formControl().setValue(res);
      if (!this.product().id)
        this.form().patchValue({ id: savedProduct.id });

      this.notificationService.success(_("common.save-successful"));
    }
  }

  showAssetLoader(asset: Asset): boolean {
    // need to check for empty product first, because on loading it loader will then show up
    if (this.loadingSig())
      return true;
    else return false
  }

  showInnerDataLoader() {
    if (this.loadingSig())
      return true;
    else return false
  }

  getProductWithoutDefaultValue() {
    let productWithoutDefaultValues = simpleDeepClone(this.product());
    if (Object.keys(productWithoutDefaultValues).length === 3) {
      delete productWithoutDefaultValues.nonCashDiscountable;
      delete productWithoutDefaultValues.nonDiscountable;
      delete productWithoutDefaultValues.nonProvisionable;
    }
    return productWithoutDefaultValues;
  }

  getAsset() {
    if (this.product())
      this.assetModel = this.product().featuredAsset;
    return this.assetModel;
  }

  // needs to be implemented for direct use without formly
  writeValue(obj: Asset): void {
    this.assetModel = obj;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  insertBreakAtDot(str: string) {
    return insertBreakAtDot(str);
  }

  getCreatorEmployeeInfo = (product?: Product) =>
    formatCreatorEmployeeName(product?.creatorEmployee ? product : this.product());

  openParentProduct(parentId: string) {
    if (parentId) {
      this.router.navigate([this.getProductEditLink(parentId)]);
    }
  }

  getProductEditLink(productId: string) {
    return EditLinks.PRODUCT + productId;
  }
}
